import { WizardState, TenantCourse } from './campaign-wizard';
import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { OntraportTagPicker } from './ontraport-tag-picker';
import { CourseSelect } from './course-select';
import {
  IcoGlobe,
  IcoCreditCard,
  IcoTag,
  IcoSparkles,
  IcoClock,
  IcoGift,
  IcoCalendar,
  IcoLock,
} from '@components/icons';
import { CAMPAIGN_BRANDS } from 'shared/campaign-brands';

type Props = {
  state: WizardState;
  courses: TenantCourse[];
  updateState: (partial: Partial<WizardState>) => void;
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

function formatPrice(price: { priceInCents?: number; paymentType: string; interval?: string }) {
  if (!price.priceInCents) {
    return 'Free';
  }
  const dollars = (price.priceInCents / 100).toFixed(price.priceInCents % 100 === 0 ? 0 : 2);
  const suffix = price.paymentType === 'subscription' && price.interval ? `/${price.interval}` : '';
  return `$${dollars}${suffix}`;
}

export function BasicsStep({ state, courses, updateState }: Props) {
  const [loadingPrices, setLoadingPrices] = useState(false);

  useEffect(() => {
    if (state.courseId && state.prices.length === 0) {
      loadPrices(state.courseId, state.selectedPriceId);
    }
  }, [state.courseId]);

  async function loadPrices(courseId: string, keepPriceId?: string) {
    setLoadingPrices(true);
    try {
      const result = await rpx.campaigns.getCampaign({ courseId });
      const existingPriceStillValid =
        keepPriceId && result.prices.some((p) => p.id === keepPriceId);
      updateState({
        prices: result.prices,
        selectedPriceId: existingPriceStillValid ? keepPriceId : (result.prices[0]?.id ?? ''),
        courseTitle: result.course.title,
      });
    } catch {
      updateState({ prices: [] });
    } finally {
      setLoadingPrices(false);
    }
  }

  function handleCourseSelect(courseId: string) {
    const course = courses.find((c) => c.id === courseId);
    if (course) {
      const slug = state.slug || slugify(course.title);
      updateState({
        courseId,
        courseTitle: course.title,
        slug,
        prices: [],
        selectedPriceId: '',
      });
      loadPrices(courseId);
    }
  }

  const selectedPrice = state.prices.find((p) => p.id === state.selectedPriceId);
  const isSubscription = selectedPrice?.paymentType === 'subscription';
  const hasFreeTrial = (state.freeTrialDays ?? 0) > 0;

  return (
    <div>
      <h2 class="text-lg font-semibold text-gray-900 mb-1">Campaign Basics</h2>
      <p class="text-sm text-gray-500 mb-5">Select your course, set the URL, and choose a price.</p>

      <div class="flex flex-col gap-5">
        <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <IcoGlobe class="size-4 text-gray-400" />
            <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              Course & URL
            </h3>
          </div>
          <div class="flex flex-col gap-1.5 max-w-lg">
            <span class="text-sm font-medium text-gray-700">Course</span>
            <CourseSelect
              courses={courses}
              value={state.courseId}
              onChange={handleCourseSelect}
              placeholder="Search and select a course..."
            />
          </div>

          <label class="flex flex-col gap-1.5 max-w-lg">
            <span class="text-sm font-medium text-gray-700">Checkout Page URL</span>
            <div class="flex items-center gap-0 border border-gray-300 rounded-lg overflow-hidden">
              <span class="bg-gray-50 text-gray-500 text-sm px-3 py-2 border-r border-gray-300 whitespace-nowrap">
                /c/
              </span>
              <input
                type="text"
                value={state.slug}
                onInput={(e) => updateState({ slug: (e.target as HTMLInputElement).value })}
                placeholder="my-event"
                class="flex-1 px-3 py-2 text-sm border-0 outline-none"
              />
            </div>
            {state.slug && (
              <span class="text-xs text-gray-500">
                {window.location.host}/c/{state.slug}
              </span>
            )}
          </label>

          <label class="flex flex-col gap-1.5 max-w-lg">
            <span class="text-sm font-medium text-gray-700">Page Title (SEO)</span>
            <input
              type="text"
              value={state.meta.pageTitle || ''}
              onInput={(e) =>
                updateState({
                  meta: { ...state.meta, pageTitle: (e.target as HTMLInputElement).value },
                })
              }
              placeholder="e.g., The 3-Day Effortless AI Event | Mirasee"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <span class="text-xs text-gray-500">Shown in the browser tab exactly as entered.</span>
          </label>

          <label class="flex flex-col gap-1.5 max-w-lg">
            <span class="text-sm font-medium text-gray-700">Meta description (SEO)</span>
            <textarea
              rows={3}
              value={state.meta.description || ''}
              onInput={(e) =>
                updateState({
                  meta: { ...state.meta, description: (e.target as HTMLTextAreaElement).value },
                })
              }
              placeholder="e.g., This is the official checkout page for Mirasee Inc."
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <span class="text-xs text-gray-500">Shown in search results and link previews.</span>
          </label>
        </div>

        <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <IcoLock class="size-4 text-gray-400" />
            <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Consent</h3>
          </div>
          <label class="flex items-start gap-3 cursor-pointer max-w-lg">
            <input
              type="checkbox"
              checked={state.simpleCheckConsent}
              onChange={(e) =>
                updateState({ simpleCheckConsent: (e.target as HTMLInputElement).checked })
              }
              class="mt-0.5 rounded border-gray-300 text-indigo-600"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm text-gray-700">Show SimpleCheck consent text</span>
              <span class="text-xs text-gray-500">
                Appears under the email field. The submit time goes to the Ontraport field
                “SimpleCheck Consent Date”.
              </span>
            </span>
          </label>
        </div>

        <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <IcoSparkles class="size-4 text-gray-400" />
            <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Branding</h3>
          </div>
          <div class="flex flex-col gap-1.5 max-w-lg">
            <span class="text-sm font-medium text-gray-700">Brand</span>
            <p class="text-xs text-gray-500">
              Choose the visual style for this campaign page — colors, fonts, and logo.
            </p>
            <div class="flex flex-col gap-2">
              {Object.values(CAMPAIGN_BRANDS).map((b) => (
                <label
                  key={b.id}
                  class={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    state.brand === b.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="brand"
                    value={b.id}
                    checked={state.brand === b.id}
                    onChange={() => updateState({ brand: b.id })}
                    class="text-indigo-600"
                  />
                  <span class="text-sm font-medium text-gray-900">{b.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <IcoCreditCard class="size-4 text-gray-400" />
            <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Pricing</h3>
          </div>
          {loadingPrices ? (
            <p class="text-sm text-gray-500">Loading prices...</p>
          ) : state.prices.length === 0 ? (
            <p class="text-sm text-gray-500">
              {state.courseId ? (
                <>
                  No prices found.{' '}
                  <a
                    href={`/manage/courses/${state.courseId}/prices/new`}
                    target="_blank"
                    rel="noreferrer"
                    class="text-indigo-600 hover:underline"
                  >
                    Create one ↗
                  </a>
                  , then reload this page.
                </>
              ) : (
                'Select a course to see available prices.'
              )}
            </p>
          ) : (
            <div class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Default Price</span>
              <p class="text-xs text-gray-500">
                Shown when visitors arrive without a specific price link.
              </p>
              <div class="flex flex-col gap-2">
                {state.prices.map((price) => {
                  const isDefault = state.selectedPriceId === price.id;
                  const isAlt = state.alternativePriceIds.includes(price.id);
                  const priceActive = isDefault || isAlt;
                  const priceTags = state.priceOntraportTags[price.id] || [];
                  return (
                    <div
                      key={price.id}
                      class={`rounded-lg border transition-colors ${
                        isDefault
                          ? 'border-indigo-500 bg-indigo-50'
                          : isAlt
                            ? 'border-indigo-300 bg-indigo-50/50'
                            : 'border-gray-200'
                      }`}
                    >
                      <div class="flex items-center gap-3 p-3">
                        <input
                          type="radio"
                          name="price"
                          value={price.id}
                          checked={isDefault}
                          onChange={() => {
                            const isRecurring =
                              price.paymentType === 'subscription' ||
                              price.paymentType === 'paymentplan';
                            updateState({
                              selectedPriceId: price.id,
                              freeTrialDays: isRecurring ? state.freeTrialDays : undefined,
                              alternativePriceIds: state.alternativePriceIds.filter(
                                (id) => id !== price.id,
                              ),
                            });
                          }}
                          class="text-indigo-600"
                        />
                        <div class="flex-1">
                          <span class="text-sm font-medium text-gray-900">
                            {formatPrice(price)}
                          </span>
                          {price.name && (
                            <span class="text-sm text-gray-500 ml-2">{price.name}</span>
                          )}
                          <span class="text-xs text-gray-400 ml-2">{price.paymentType}</span>
                        </div>
                        {!isDefault && (
                          <label class="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isAlt}
                              onChange={(e) => {
                                const checked = (e.target as HTMLInputElement).checked;
                                updateState({
                                  alternativePriceIds: checked
                                    ? [...state.alternativePriceIds, price.id]
                                    : state.alternativePriceIds.filter((id) => id !== price.id),
                                });
                              }}
                              class="text-indigo-600"
                            />
                            <span class="text-xs text-gray-500">Also offer</span>
                          </label>
                        )}
                      </div>
                      {priceActive && state.courseId && (
                        <div class="px-3 pb-3 pt-1 border-t border-gray-200/60 bg-white/50">
                          <div class="flex flex-col gap-1.5">
                            <span class="text-xs font-medium text-gray-600">
                              Ontraport Tags for this Price
                            </span>
                            <p class="text-xs text-gray-500">
                              Applied only when a buyer purchases this price. Added on top of the
                              campaign tags below.
                            </p>
                            <OntraportTagPicker
                              courseId={state.courseId}
                              selectedTags={priceTags}
                              onChange={(tags) =>
                                updateState({
                                  priceOntraportTags: {
                                    ...state.priceOntraportTags,
                                    [price.id]: tags,
                                  },
                                })
                              }
                              placeholder="Search purchase tags..."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {selectedPrice && isSubscription && (
          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoGift class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Free Trial
              </h3>
            </div>
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasFreeTrial}
                onChange={(e) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  updateState({ freeTrialDays: checked ? 14 : undefined });
                }}
                class="rounded border-gray-300 text-indigo-600"
              />
              <span class="text-sm text-gray-700">Start with a free trial</span>
            </label>
            {hasFreeTrial && (
              <div class="flex flex-col gap-2 ml-7">
                <label class="flex items-center gap-2">
                  <span class="text-sm text-gray-600">Trial length:</span>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={state.freeTrialDays}
                    onInput={(e) => {
                      const val = parseInt((e.target as HTMLInputElement).value, 10);
                      if (!isNaN(val) && val > 0) {
                        updateState({ freeTrialDays: val });
                      }
                    }}
                    class="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-20 text-center"
                  />
                  <span class="text-sm text-gray-600">days</span>
                  {(state.freeTrialDays ?? 0) % 7 === 0 && (state.freeTrialDays ?? 0) > 0 && (
                    <span class="text-xs text-gray-400">
                      ({(state.freeTrialDays ?? 0) / 7} weeks)
                    </span>
                  )}
                </label>
                <p class="text-xs text-gray-500">
                  After the trial, the buyer will be charged {formatPrice(selectedPrice)}{' '}
                  automatically.
                </p>
              </div>
            )}
          </div>
        )}

        {selectedPrice && (
          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoCalendar class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Cohort Start Date (optional)
              </h3>
            </div>
            <p class="text-xs text-gray-500">
              For cohort-based campaigns, the date the cohort begins. Used for admin display and
              welcome-email tokens. Buyers are always charged at signup and get course access
              immediately — module release dates control content drip.
            </p>
            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="primary-enrollment-timing"
                  checked={!state.deferredDate}
                  onChange={() => updateState({ deferredDate: '' })}
                  class="text-indigo-600"
                />
                <span class="text-sm text-gray-700">No cohort start date</span>
              </label>
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="primary-enrollment-timing"
                  checked={!!state.deferredDate}
                  onChange={() => {
                    if (!state.deferredDate) {
                      const d = new Date();
                      d.setDate(d.getDate() + 14);
                      updateState({
                        deferredDate: d.toISOString().slice(0, 10),
                      });
                    }
                  }}
                  class="text-indigo-600"
                />
                <span class="text-sm text-gray-700">Cohort starts on a specific date</span>
              </label>
            </div>
            {state.deferredDate && (
              <div class="flex flex-col gap-2 ml-7">
                <input
                  type="date"
                  value={state.deferredDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onInput={(e) =>
                    updateState({
                      deferredDate: (e.target as HTMLInputElement).value,
                    })
                  }
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm max-w-xs"
                />
              </div>
            )}
          </div>
        )}

        <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <IcoClock class="size-4 text-gray-400" />
            <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              Campaign End Redirect
            </h3>
          </div>
          <div class="flex items-center justify-between">
            <p class="text-xs text-gray-500">
              Automatically redirect visitors to another page after the campaign ends.
            </p>
            <button
              type="button"
              role="switch"
              aria-checked={state.redirectEnabled}
              class={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors ${
                state.redirectEnabled ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
              onClick={() => updateState({ redirectEnabled: !state.redirectEnabled })}
            >
              <span
                class={`pointer-events-none inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
                  state.redirectEnabled ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {state.redirectEnabled && (
            <div class="flex flex-col gap-4 max-w-lg">
              {state.redirectAt && new Date(state.redirectAt) <= new Date() && (
                <div class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  This redirect is currently active — visitors are being redirected to{' '}
                  {state.redirectUrl || 'the configured URL'}.
                </div>
              )}
              <label class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-gray-700">Redirect URL</span>
                <input
                  type="url"
                  value={state.redirectUrl}
                  onInput={(e) =>
                    updateState({ redirectUrl: (e.target as HTMLInputElement).value })
                  }
                  placeholder="https://mirasee.com"
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <label class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-gray-700">Redirect after</span>
                <input
                  type="datetime-local"
                  value={state.redirectAt}
                  onInput={(e) => updateState({ redirectAt: (e.target as HTMLInputElement).value })}
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <span class="text-xs text-gray-500">
                  Your local time. Visitors will be redirected after this date.
                </span>
              </label>
            </div>
          )}
        </div>

        {state.courseId && (
          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoTag class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Integrations
              </h3>
            </div>
            <div class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Campaign-Wide Ontraport Tags</span>
              <p class="text-xs text-gray-500">
                Applied to every buyer who purchases through this campaign, regardless of the price
                they choose. Add per-price tags above to differentiate (e.g. Full Pay vs. Payment
                Plan).
              </p>
              <OntraportTagPicker
                courseId={state.courseId}
                selectedTags={state.ontraportTags}
                onChange={(tags) => updateState({ ontraportTags: tags })}
                placeholder="Search purchase tags..."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
